import { createEngine, type Engine } from './engine.js';
import { ScriptedDice } from './scriptedDice.js';
import type { BetId, BetOptions, BetType, CrapsSnapshot, DieFace, TableRules, TableState } from './types.js';

/**
 * The minimal, replayable record of USER ACTIONS. Emitted by the engine on its
 * subscribeInputs channel (one per public command). A fresh engine fed this
 * list — in order, with a ScriptedDice carrying the logged faces — reconstructs
 * exact table state. Discriminated by `type` so it rides the same wire / D1
 * storage that keys on event_type.
 */
export type EngineInput =
  | { type: 'bet_placed'; betType: BetType; amount: number; options: BetOptions }
  | { type: 'bet_removed'; id: BetId }
  | { type: 'bet_forfeited'; id: BetId }
  | { type: 'working_set'; id: BetId; working: boolean }
  | { type: 'roll'; d1: DieFace; d2: DieFace };

/** Apply one input to an engine. For `roll`, push the logged faces onto the
 *  engine's ScriptedDice, then roll so the engine draws exactly those faces. */
export function applyInput(engine: Engine, dice: ScriptedDice, input: EngineInput): void {
  switch (input.type) {
    case 'bet_placed':
      engine.placeBet(input.betType, input.amount, input.options);
      break;
    case 'bet_removed':
      engine.removeBet(input.id);
      break;
    case 'bet_forfeited':
      engine.forfeitBet(input.id);
      break;
    case 'working_set':
      engine.setWorking(input.id, input.working);
      break;
    case 'roll':
      dice.enqueue(input.d1, input.d2);
      engine.roll();
      break;
  }
}

/**
 * Reconstruct a single shooter turn: hydrate a fresh engine from the turn's
 * keyframe (full serialized state at turn start), then apply the turn's inputs.
 * `rules` is required because the keyframe (a CrapsSnapshot) does not carry
 * them; pass the game's rules. Returns the engine state after the turn.
 */
export function replayTurn(
  rules: TableRules,
  keyframe: CrapsSnapshot,
  inputs: EngineInput[],
): TableState {
  // A turn keyframe is always a full serialize(), which includes bankroll.
  // Refuse a bankroll-less snapshot rather than silently replaying with 0.
  if (keyframe.bankroll === undefined) {
    throw new Error('replayTurn: keyframe must include bankroll');
  }
  const dice = new ScriptedDice();
  const engine = createEngine({ dice, rules, bankroll: keyframe.bankroll });
  engine.hydrate(keyframe);
  for (const input of inputs) applyInput(engine, dice, input);
  return engine.getState();
}

/** Reconstruct table state from a starting header + an ordered input log. */
export function rebuildState(
  header: { bankroll: number; rules: TableRules },
  inputs: EngineInput[],
): TableState {
  const dice = new ScriptedDice();
  const engine = createEngine({ dice, rules: header.rules, bankroll: header.bankroll });
  for (const input of inputs) applyInput(engine, dice, input);
  return engine.getState();
}
